import React, { useState, useEffect } from 'react';
import { useAppContext } from 'context/AppContext';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { getBooking, updateBooking, createBookingNote, synchronizeLockSystemAccess, changeStorage } from 'actions/admin/bookings';
import { fetchStorageSite } from 'actions/storageSites';
import { searchBookings } from 'actions/admin/bookings';
import { handleResponse } from 'actions/actionHelpers';
import routes from 'routes';
import { formatIsoDate } from 'helpers/DateHelper';
import { createUpdateBookingRequest } from './AdminBookingHelper';
import lockTypes from 'enums/lockTypes';
import adminBookingStatuses from 'enums/adminBookingStatuses';

import EditBookingForm from './EditBookingForm';
import Loader from 'common/Loader';
import AdminPage from '../../presentational/AdminPage';
import AdminContentWrapper from '../../presentational/AdminContentWrapper';
import AdminActionWrapper from '../../presentational/AdminActionWrapper';
import Button from '@material-ui/core/Button';
import { Link } from 'react-router-dom';
import ChangeStorageDialog from './ChangeStorageDialog';
import EditNoteDialog from 'components/EditNoteDialog';

const Booking = () => {
    const { appContext } = useAppContext();
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();

    const [booking, setBooking] = useState(undefined);
    const [storageSite, setStorageSite] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [bookingNoteDialogOpen, setBookingNoteDialogOpen] = useState(false);
    const [changeStorageDialogOpen, setChangeStorageDialogOpen] = useState(false);

    useEffect(() => {
        loadBooking();
    }, [params.bookingId]);

    const loadBooking = () => {
        setIsLoading(true);
        dispatch(getBooking(params.bookingId))
            .then(handleResponse(
                response1 => {
                    setBooking(response1.payload);
                    dispatch(fetchStorageSite(
                        response1.payload.storageSiteId,
                        formatIsoDate(new Date(), appContext), // includeBookingCalendarFrom
                        undefined, // includeBookingCalendarTo
                        true, // includeStorageGroupBookingCalendars
                        false,
                        false,
                        params.bookingId
                    ))
                        .then(handleResponse(
                            response2 => {
                                setStorageSite(response2.payload);
                                setIsLoading(false);
                            },
                            () => setIsLoading(false)
                        ));
                },
                () => setIsLoading(false)
            ));
    };

    const handleSubmit = formValues => {
        setIsSubmitting(true);
        const updateBookingRequest = createUpdateBookingRequest(formValues, storageSite, appContext);
        dispatch(updateBooking(booking.id, updateBookingRequest))
            .then(handleResponse(
                () => {
                    history.push(routes.admin.bookings);
                    setIsSubmitting(false);
                    return { snackbar: { message: 'Booking was successfully saved.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const handleCancel = () => {
        history.push(routes.admin.bookings);
    };

    const handleChangeStorageButtonClick = () => {
        setChangeStorageDialogOpen(true);
    };

    const handleAddBookingNoteButtonClick = () => {
        setBookingNoteDialogOpen(true);
    };

    const handleSynchronizeLockSystemButtonAccess = () => {
        setIsLoading(true);
        dispatch(synchronizeLockSystemAccess(params.bookingId))
            .then(handleResponse(
                response => {
                    setIsLoading(false);
                    if(response.payload[0].success) {
                        return { snackbar: { message: 'Lock system access was synchronized.' } };
                    }
                    return { dialog: { title: 'Lock system access synchronization failed', body: response.payload[0].errorMessage, severity: 'error' } };
                },
                () => {
                    setIsLoading(false);
                }
            ));
    };

    const handleChangeStorageDialogSubmit = formValues => {
        setChangeStorageDialogOpen(false);
        setIsSubmitting(true);
        const request = {
            startDate: formatIsoDate(formValues.startDate),
            storageGroupId: formValues.storageGroupId,
            storageId: formValues.storageId
        };
        dispatch(changeStorage(params.bookingId, request))
            .then(handleResponse(
                response => {
                    setIsSubmitting(false);
                    const newBooking = response.payload;
                    const newBookingCreated = newBooking.id !== booking.id;
                    history.push(routes.admin.booking.replace(':bookingId', newBooking.id));
                    return { snackbar: { message: `The storage was successfully changed.${(newBookingCreated ? ' A new booking was created.' : '')}` } };
                },
                () => {
                    setIsSubmitting(false);
                }
            ));
    };

    const handleChangeStorageDialogCancel = () => {
        setChangeStorageDialogOpen(false);
    };

    const handleBookingNoteDialogSubmit = (formValues, form) => {
        const request = {
            text: formValues.text,
            flagged: formValues.flagged,
            isSuperAdminNote: true
        };
        dispatch(createBookingNote(params.bookingId, request))
            .then(handleResponse(
                response => {
                    const newBooking = { ...booking, flagged: request.flagged };
                    newBooking.notes.push(response.payload);
                    setBooking(newBooking);
                    form.reset();
                    setBookingNoteDialogOpen(false);
                    dispatch(searchBookings());
                    return { snackbar: { message: 'Note was successfully saved.' } };
                }
            ));
    };

    const handleBookingNoteDialogCancel = () => {
        setBookingNoteDialogOpen(false);
    };

    const title = 'Booking: ' + (booking && storageSite ? booking.id : 'Loading...');

    const showChangeStorageButton =
        booking &&
        [adminBookingStatuses.notStarted.key, adminBookingStatuses.active.key].some(o => o === booking.adminBookingStatus) &&
        !booking.hasBeenExtended;

    return (
        <AdminPage title={title}>
            {
                isLoading && <Loader />
            }
            {
                !isLoading && booking && storageSite &&
                (
                    <AdminContentWrapper type="detail">

                        <EditBookingForm
                            booking={booking}
                            storageSite={storageSite}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            isSubmitting={isSubmitting}
                        />

                        <AdminActionWrapper>
                            {
                                showChangeStorageButton &&
                                (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleChangeStorageButtonClick}
                                    >
                                        Change storage
                                    </Button>
                                )
                            }
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddBookingNoteButtonClick}
                            >
                                Add note
                            </Button>
                            {
                                booking.lockCodeUrl &&
                                (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        component={Link}
                                        to={{ pathname: booking.lockCodeUrl }}
                                        target="_blank"
                                    >
                                        Show lock code
                                    </Button>
                                )
                            }
                            {
                                booking.lockTypes.some(o => lockTypes[o]?.isSynchronizable) &&
                                (
                                    <>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSynchronizeLockSystemButtonAccess}
                                        >
                                            Synchronize lock system access
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            component={Link}
                                            to={routes.admin.createShowing.replace(':bookingId', booking.id)}
                                        >
                                            Create showing
                                        </Button>
                                    </>
                                )
                            }
                        </AdminActionWrapper>

                    </AdminContentWrapper>
                )
            }
            {
                booking && storageSite &&
                (
                    <ChangeStorageDialog
                        booking={booking}
                        storageSite={storageSite}
                        open={changeStorageDialogOpen}
                        onSubmit={handleChangeStorageDialogSubmit}
                        onCancel={handleChangeStorageDialogCancel}
                    />
                )
            }
            <EditNoteDialog
                open={bookingNoteDialogOpen}
                showFlagged
                flagged={booking?.flagged}
                maxLength={1024}
                onSubmit={handleBookingNoteDialogSubmit}
                onCancel={handleBookingNoteDialogCancel}
            />
        </AdminPage>
    );
};

export default Booking;
